import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import PlmBatchListPage from './PlmBatchListPage';
import PlmResultEntryPage from './PlmResultEntryPage';
import PlmQcListPage from './PlmQcListPage';
import PlmDailyQcPage from './PlmDailyQcPage';
import { PLM_VALIDATE_MODE_ENABLED, plmListTitle } from './plmWorkflow';
import './plm.css';

/**
 * PLM — the nav entry point, with prototype v13's MODES (2026-09-28):
 *
 *   View      every PLM QC batch → double-click → the detail, READ-ONLY
 *   Enter     the batches still open for result entry → the editable detail
 *             (also what the QC Batch form opens directly, bypassing this list)
 *   Validate  (built 2026-09-29) the batches Pending SDMS Validation → Validate /
 *             Reject the ticked rows, or double-click → the detail, read-only,
 *             with Validate / Reject and the package's Check Points. Runs on the
 *             SHARED SDMS Result Validation endpoints, so what follows is the
 *             review chain Default Settings switch on.
 *             ON since 2026-10-10 (PLM_VALIDATE_MODE_ENABLED; hidden from
 *             2026-09-30 until then). SDMS Result Validation still validates
 *             a PLM batch too.
 *   QC        the QC Results list (Reference / Intra / Inter) + "+ New QC";
 *             a Reference record opens the Daily QC screen
 *
 * The switch sits in the app's TOP BAR, right after the title, on the lists AND
 * on the batch detail — the prototype's top banner (`#modSeg` + `#qcBtn`, owner
 * report 2026-09-29: "this part is missing"). Switching from the detail follows
 * the prototype's `setMode`: Enter keeps the batch open (editable); View,
 * Validate and QC go to that mode's list — after the shared unsaved-changes
 * confirm (standing rule 15).
 *
 * List → (pick) → detail is the only flow (standing rule 10); leaving the
 * detail returns to the list of the mode it was opened from.
 */
export type PlmMode = 'view' | 'enter' | 'validate' | 'qc';

/** The three batch modes — one segmented control. QC is its own button. */
type ModeDef = { id: Exclude<PlmMode, 'qc'>; label: string; title: string };
const ALL_MODES: ModeDef[] = [
  { id: 'view', label: 'View', title: 'Every PLM batch, read-only' },
  { id: 'enter', label: 'Enter', title: 'Batches open for result entry' },
  { id: 'validate', label: 'Validate', title: 'Batches pending validation — Validate or Reject' },
];
/** Validate is offered only while enabled (PLM_VALIDATE_MODE_ENABLED — on
 *  since 2026-10-10). */
const MODES = ALL_MODES.filter(m => m.id !== 'validate' || PLM_VALIDATE_MODE_ENABLED);

/** How a batch detail is opened — the list mode it came from. */
type OpenMode = 'view' | 'enter' | 'validate';

export interface PlmPageProps {
  /** Open straight on this batch (Enter mode) — how QC Batches opens PLM. */
  seedBatchOid?: string;
  /** Back from the SEEDED batch leaves PLM (back to QC Batches, as G5's Back
   *  does) instead of landing on the PLM list. */
  onExit?: () => void;
}

export default function PlmPage({ seedBatchOid, onExit }: PlmPageProps) {
  const [mode, setMode] = useState<PlmMode>('enter');
  const [batchOid, setBatchOid] = useState<string | null>(seedBatchOid ?? null);
  // The mode the detail was opened in — View and Validate open it read-only.
  const [openMode, setOpenMode] = useState<OpenMode>('enter');
  // The layer to land on — a QC record's re-read, or one just started.
  const [openSeq, setOpenSeq] = useState<string | undefined>(undefined);
  // Bumped by every Retrieve, so the entry screen reloads even for the batch already open.
  const [retrieveCount, setRetrieveCount] = useState(0);

  // The Daily QC screen (a record, or null = the slide picker), opened from QC mode.
  const [dailyOpen, setDailyOpen] = useState<{ oid: string | null } | null>(null);

  // A LIST names itself in the top bar, as the prototype does (v13 :2877) —
  // "PLM – QC Batches" · "PLM – Validate" · "PLM – QC Results". An open batch
  // sets its own title, so this one is released while it is up.
  const listTitle = batchOid || dailyOpen ? null : plmListTitle(mode);
  useEffect(() => {
    if (!listTitle) return;
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: listTitle }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [listTitle]);

  const open = (oid: string, as: OpenMode, seq?: string) => {
    setOpenMode(as); setOpenSeq(seq); setBatchOid(oid);
  };

  /** Mode switch pressed while a batch (or a Daily QC) is open. */
  const switchFromDetail = (m: PlmMode) => {
    if (batchOid && m === openMode && !dailyOpen) return;
    // View → Enter keeps the SAME batch and makes it editable; a read-only view
    // has nothing unsaved, so there is nothing to confirm.
    if (batchOid && !dailyOpen && m === 'enter') { setOpenMode('enter'); return; }
    void guardedExit(() => {
      setBatchOid(null);
      setOpenSeq(undefined);
      setDailyOpen(null);
      setMode(m);
    });
  };

  if (dailyOpen) {
    return (
      <>
        <PlmTopbarModes mode="qc" onChange={switchFromDetail} />
        <PlmDailyQcPage oid={dailyOpen.oid} onBack={() => setDailyOpen(null)} />
      </>
    );
  }

  if (batchOid) {
    return (
      <PlmResultEntryPage
        // A batch opened from the Retrieve bar starts afresh (its own layers, its own draft).
        key={`${batchOid}:${retrieveCount}`}
        batchOid={batchOid}
        mode={openMode}
        initialSequenceOid={openSeq}
        modeSwitch={<PlmModeBar mode={openMode} onChange={switchFromDetail} />}
        onEnterMode={() => setOpenMode('enter')}
        onRetrieve={(oid, as) => {
          // G5 asks to save first; G6 uses the shared unsaved-changes confirm (standing rule 15).
          void guardedExit(() => {
            setOpenSeq(undefined);
            setOpenMode(as);
            setBatchOid(oid);
            setRetrieveCount(n => n + 1);
          });
        }}
        onBack={() => {
          if (onExit && batchOid === seedBatchOid) { onExit(); return; }
          setBatchOid(null); setOpenSeq(undefined);
        }}
        // Validate / Reject stay in PLM, on the Validate list - even for a batch opened from the QC Batch form
        // (owner 2026-10-19: "after validate this form showing automatically"; G5 OpenEmpty(Validate)).
        onValidateDone={() => { setBatchOid(null); setOpenSeq(undefined); setMode('validate'); }}
      />
    );
  }

  const modes = <PlmTopbarModes mode={mode} onChange={setMode} />;
  if (mode === 'qc') {
    return (
      <>
        {modes}
        <PlmQcListPage onOpenBatch={(oid, seq) => open(oid, 'enter', seq)}
          onOpenDailyQc={oid => setDailyOpen({ oid })} />
      </>
    );
  }
  return (
    <>
      {modes}
      <PlmBatchListPage
        key={mode}
        mode={mode}
        onOpen={(oid, as) => open(oid, as ?? mode)}
      />
    </>
  );
}

/**
 * The v13 mode switch — the prototype's `#modSeg` (View · Enter · Validate, one
 * segmented control) followed by its separate `#qcBtn` (QC, purple when on).
 */
export function PlmModeBar({ mode, onChange }: { mode: PlmMode; onChange: (m: PlmMode) => void }) {
  return (
    <span className="plm-modeswitch">
      <span className="plm-charts__seg plm-modebar" role="tablist" aria-label="PLM mode">
        {MODES.map(m => (
          <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} title={m.title}
            className={`plm-charts__segbtn${mode === m.id ? ' is-active' : ''}`}
            onClick={() => onChange(m.id)}>{m.label}</button>
        ))}
      </span>
      <button type="button" aria-pressed={mode === 'qc'}
        className={`ca-btn plm-qcbtn${mode === 'qc' ? ' is-on' : ''}`}
        title="QC Result list — Reference, Intra-dup, Inter-dup"
        onClick={() => onChange('qc')}>QC</button>
    </span>
  );
}

/**
 * Docks the mode switch in the app top bar, right after the title — AppShell's
 * `#ai-topbar-meta` slot, the prototype's banner position. Used by the list
 * pages; the batch detail passes the switch to its own PlmTopbarMeta instead,
 * so it shares ONE portal with the load-time chip and keeps the prototype order
 * (title · modes · QC · Loaded in …).
 */
function PlmTopbarModes(props: { mode: PlmMode; onChange: (m: PlmMode) => void }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setSlot(document.getElementById('ai-topbar-meta')); }, []);
  return slot ? createPortal(<PlmModeBar {...props} />, slot) : null;
}

